import React from "react";
import Link from "next/link";
import { Card, CardContent, CardTitle, CardDescription } from "@/components/ui/card";
import { SectionHeader } from "../components/section-header";
import { SectionLoader } from "../components/section-loader";
import { EmptyState } from "@/components/shared/empty-state";
import { Building2, Users, ArrowRight } from "lucide-react";

interface FeaturedOrganizationsSectionProps {
  items: any[];
  isLoading: boolean;
}

export function FeaturedOrganizationsSection({ items, isLoading }: FeaturedOrganizationsSectionProps) {
  const displayItems = items.length > 0 ? items.slice(0, 4) : [];

  if (!displayItems.length && !isLoading) return null;

  return (
    <section className="border-b border-border bg-neutral-50/50" aria-labelledby="organizations-heading">
      <div className="mx-auto max-w-7xl px-5 py-16 sm:py-20">
        <SectionHeader
          eyebrow="Organizations"
          icon={Building2}
          title="Featured Organizations"
          href="/organizations"
        />
        {isLoading ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-40 bg-neutral-100 rounded-xl animate-pulse" />
            ))}
          </div>
        ) : displayItems.length > 0 ? (
          <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
            {displayItems.map((item: any) => (
              <Link key={item.id} href={`/organizations/${item.slug ?? item.id}`}>
                <Card className="group h-full hover:border-neutral-300 transition-all duration-150">
                  <CardContent className="p-5">
                    <div className="flex items-center gap-3 mb-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-neutral-100 text-foreground">
                        <Building2 className="h-6 w-6" />
                      </div>
                      <div>
                        <CardTitle className="text-sm">{item.name}</CardTitle>
                        <CardDescription className="text-xs mt-0.5">
                          {item.industry ?? "Organization"}
                        </CardDescription>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-muted-foreground">
                      <Users className="h-3 w-3" />
                      <span>{item.memberCount ?? 0} followers</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>
        ) : (
          <EmptyState icon={Building2} description="No organizations yet." />
        )}
      </div>
    </section>
  );
}